import * as React from "react";
import { RenderContainer } from "@skbkontur/react-ui/internal/RenderContainer";
import debounce from "lodash.debounce";
import { Button, Link } from "react-ui";
import ReactMarkdown from "react-markdown";

import { useUnitLayoutingContext } from "../Unit/Internals/UnitBorderContext/UnitLayoutingContext";
import { PopupHelper } from "../../ValueEditors/PopupTextArea/PopupHelper";
import { LayoutEvents } from "../../Internals/LayoutEvents/LayoutEvents";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { BANNER_CLICK } from "../../metricsAction";
import { useCandyTheme } from "../../Internals/ThemeProvider/CandyTheme/UseCandyTheme";
import { CloseCrossIcon } from "../../Internals/CloseCrossIcon/CloseCrossIcon";

import {
    PopupBanner,
    BannerContainer,
    BannerAside,
    BannerHeader,
    ContentContainer,
    bannerFullHeight,
    BannerClose,
    BannerIcon,
    bannerHeight,
    BannerIconForPortrait,
    BannerContent,
} from "./Banner.styles";

export interface BannerProps {
    children?: React.ReactNode;
    name: string;
    customSugar?: boolean;
    header?: string;
    text?: string;
    href?: string;
    clickableText?: string;
    useButton?: boolean;
    bannerTheme?: string;
    allowClose?: boolean;
    isOpen?: boolean;
    setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    onClose?: () => void;
    arrowInButton?: boolean;
    iconSrc?: string;
    isReadOnly?: boolean;
    isPortrait?: boolean;
    onClick?: () => void;
}

export function Banner(props: BannerProps): JSX.Element {
    if (!props.isReadOnly && props.isOpen && props.setIsOpen && props.onClose) {
        return <RenderBanner {...props} />;
    } else {
        return <></>;
    }
}

export const RenderBanner = (props: BannerProps): JSX.Element => {
    const colorScheme = props.bannerTheme === "dark" ? "dark" : "light";
    const theme = useCandyTheme().banner[colorScheme];
    const { addMarginTop, marginTop, unitContextRef } = useUnitLayoutingContext();
    const [coords, setCoords] = React.useState({ left: 0, top: 0, width: 0 });
    const clickableText = props.clickableText ?? "Открыть статью";
    const track = useMetrics();

    React.useEffect(() => {
        addMarginTop?.(bannerFullHeight);
        return () => addMarginTop?.(-bannerFullHeight);
    }, []);

    React.useEffect(() => {
        updateCoords();
    }, [marginTop]);

    const updateCoords = () => {
        if (unitContextRef?.current != undefined) {
            const rect = PopupHelper.getElementAbsoluteRect(unitContextRef.current);
            if (rect.left !== coords.left || rect.top !== coords.top) {
                setCoords(rect);
            }
        }
    };

    React.useEffect(() => {
        const layoutEventsSubscription = LayoutEvents.addListener(debounce(updateCoords, 200));
        return () => {
            layoutEventsSubscription.remove();
        };
    }, []);

    React.useEffect(() => {
        updateCoords();
    }, [unitContextRef?.current]);

    const onLinkClick = () => {
        track(BANNER_CLICK, { name: props.name });
    };

    const textNode = (
        <ReactMarkdown
            components={{
                p: ({ children }) => <span>{children}</span>,
                a: ({ href, children }) => (
                    <Link
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            color: `${theme.color}`,
                            textDecoration: "underline",
                        }}>
                        {children}
                    </Link>
                ),
            }}>
            {props.text ?? ""}
        </ReactMarkdown>
    );

    return (
        <RenderContainer>
            <PopupBanner
                style={{
                    width: `${coords?.width - 60}px`,
                    top: `${coords?.top - 125}px`,
                    left: `${coords?.left}px`,
                    color: `${theme.color}`,
                    backgroundColor: `${theme.backgroundColor}`,
                }}>
                {props.customSugar ? (
                    props.children
                ) : (
                    <BannerContainer data-banner-name={props.name}>
                        <ContentContainer>
                            <BannerIcon>
                                {props.iconSrc != undefined &&
                                    (props.isPortrait ? (
                                        <BannerIconForPortrait>
                                            <img src={props.iconSrc} height="130" alt={"icon"} />
                                        </BannerIconForPortrait>
                                    ) : (
                                        <img src={props.iconSrc} width="64" alt={"icon"} />
                                    ))}
                            </BannerIcon>
                            <div>
                                <BannerHeader>{props.header}</BannerHeader>
                                <BannerContent>{textNode}</BannerContent>
                            </div>
                        </ContentContainer>
                        <BannerAside>
                            {props.href && (
                                <Link onClick={onLinkClick} target="_blank" href={props.href}>
                                    {props.useButton ? (
                                        <Button size={"medium"}>{clickableText}</Button>
                                    ) : (
                                        <>{clickableText}</>
                                    )}
                                </Link>
                            )}
                            {props.onClick && !props.href && (
                                <Button
                                    size={"medium"}
                                    onClick={() => {
                                        props.onClick?.();
                                        onLinkClick();
                                    }}>
                                    {clickableText}
                                </Button>
                            )}
                        </BannerAside>
                        {props.allowClose && (
                            <BannerClose
                                onClick={() => {
                                    addMarginTop?.(-1 * bannerHeight);
                                    props.setIsOpen?.(false);
                                    props.onClose?.();
                                }}>
                                <CloseCrossIcon color={theme.closeCrossIconColor} />
                            </BannerClose>
                        )}
                    </BannerContainer>
                )}
            </PopupBanner>
        </RenderContainer>
    );
};
